import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";

import { ChatContext } from "../../contexts/chat/context";

const relativeTime = (timestamp: string): string => {
  const value = Date.parse(timestamp);
  if (!Number.isFinite(value)) return "Recently";

  const elapsed = Math.max(0, Date.now() - value);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year:
      new Date(value).getFullYear() === new Date().getFullYear()
        ? undefined
        : "numeric",
  });
};

export const PlatformConversationPicker = ({
  disabled,
  runLabel,
  scopeKey,
}: {
  disabled: boolean;
  runLabel: string;
  scopeKey: string;
}) => {
  const {
    platformConversationHistory = [],
    platformConversationHistoryLoading = false,
    platformConversationHistoryError = null,
    refreshPlatformConversationHistory,
    resumePlatformConversation,
  } = useContext(ChatContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setAnchorEl(null);
    setOpeningKey(null);
  }, [scopeKey]);

  const openPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    void refreshPlatformConversationHistory?.();
  };

  const closePicker = () => {
    setAnchorEl(null);
  };

  const openConversation = async (key: string) => {
    if (!resumePlatformConversation || openingKey) return;
    setOpeningKey(key);
    const resumed = await resumePlatformConversation(key);
    setOpeningKey(null);
    if (resumed) setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Conversation history">
        <span>
          <IconButton
            aria-label="Open conversation history"
            aria-controls={open ? "nucleus-conversation-history" : undefined}
            aria-haspopup="dialog"
            aria-expanded={open ? "true" : undefined}
            size="small"
            disabled={disabled}
            onClick={openPicker}
          >
            <HistoryRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        id="nucleus-conversation-history"
        open={open}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Recent Nucleus conversations",
            sx: {
              width: 360,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: 440,
              mt: 0.75,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 14px 40px rgba(15, 23, 42, 0.16)",
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
            Recent conversations
          </Typography>
          <Typography
            sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.78rem" }}
          >
            This customer · {runLabel}
          </Typography>
        </Box>

        {platformConversationHistoryLoading ? (
          <Box
            role="status"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 4,
            }}
          >
            <CircularProgress size={24} />
            <Typography sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
              Loading conversations...
            </Typography>
          </Box>
        ) : platformConversationHistoryError ? (
          <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
            <Typography sx={{ color: "text.secondary", fontSize: "0.84rem" }}>
              {platformConversationHistoryError}
            </Typography>
            <Button
              size="small"
              onClick={() => void refreshPlatformConversationHistory?.()}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Try again
            </Button>
          </Box>
        ) : platformConversationHistory.length === 0 ? (
          <Box sx={{ px: 3, py: 3.5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.88rem" }}>
              No earlier conversations for this run
            </Typography>
            <Typography
              sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.78rem" }}
            >
              Start a chat and it will appear here.
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ pb: 0.75, overflowY: "auto" }}>
            {platformConversationHistory.map((conversation) => {
              const canOpen =
                conversation.canContinue && conversation.state !== "running";
              const isOpening = openingKey === conversation.key;
              return (
                <ListItemButton
                  key={conversation.key}
                  disabled={!canOpen || Boolean(openingKey)}
                  selected={conversation.isCurrent}
                  onClick={() => void openConversation(conversation.key)}
                  sx={{
                    alignItems: "flex-start",
                    mx: 0.75,
                    mb: 0.25,
                    px: 1.25,
                    py: 1.1,
                    borderRadius: 1.5,
                  }}
                >
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 650,
                            fontSize: "0.84rem",
                          }}
                        >
                          {conversation.title}
                        </Typography>
                        {conversation.isCurrent && (
                          <Chip
                            size="small"
                            label="Current"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.66rem" }}
                          />
                        )}
                        {isOpening ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Typography
                            sx={{
                              flexShrink: 0,
                              color: "text.secondary",
                              fontSize: "0.7rem",
                            }}
                          >
                            {relativeTime(conversation.updatedAt)}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: "block", mt: 0.4 }}>
                        {conversation.preview && (
                          <Typography
                            component="span"
                            sx={{
                              display: "-webkit-box",
                              overflow: "hidden",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 2,
                              color: "text.secondary",
                              fontSize: "0.76rem",
                              lineHeight: 1.35,
                            }}
                          >
                            {conversation.preview}
                          </Typography>
                        )}
                        {!canOpen && (
                          <Typography
                            component="span"
                            sx={{
                              display: "block",
                              mt: conversation.preview ? 0.35 : 0,
                              color: "text.secondary",
                              fontSize: "0.72rem",
                            }}
                          >
                            {conversation.state === "running"
                              ? "Response still in progress"
                              : "This conversation can't be reopened"}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};
